/**
 * 账号 / 角色上下文。
 *
 * 原站接的是鹰角账号 SDK（HG_ACCOUNT_SDK）。这里抽成同一个接口形状：
 *  - 有 SDK 时走真实登录流程
 *  - 没有时使用一组占位账号（见下方 SNAPSHOT_*），
 *    因为原站的登录依赖外部浏览器登录与 token，无法在本地离线复刻
 *
 * 角色显示规则与线上一致：nickName 为空回退为"博士"，
 * 展示时按 '#' 切分（原站 t.replace(/\{nickname\}/g, nickName.split('#')[0] || '博士')）。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Account, AccountState, Role } from '../../types';
import { COPY } from '../../types';
import snapshot from '../data/lynchpin-api.json';

/**
 * 离线使用的占位账号。
 *
 * 抓取时页面上的真实账号信息（hgId、脱敏手机号、真实 uid）已替换为占位值 ——
 * 那是真实个人信息，不应进入代码仓库。占位值保留了原始的**形状**，
 * 因此昵称展示规则（`nickName.split('#')[0]`、空值回退"博士"）依然能被完整演示。
 *
 * 若存在 window.HG_ACCOUNT_SDK，则走真实 SDK，不使用这里的占位值。
 */
export const SNAPSHOT_ROLE: Role = {
  uid: '100000000',
  nickName: '博士#0001',
  channelMasterId: 1,
};

const SNAPSHOT_ACCOUNT: Account = {
  hgId: '0000000000000',
  phone: '138****0000',
  email: null,
};

export interface AccountContextValue extends AccountState {
  login: () => void;
  logout: () => Promise<void>;
  switchRole: () => void;
  /** 形如 "null#7393"；无角色时为 null */
  displayName: string | null;
  /** 原站的 nickname 归一化：null / 空 -> "博士" */
  nickname: string;
  /** 数据来源：real=接了真实 SDK；snapshot=使用抓取的账号快照 */
  mode: 'real' | 'snapshot';
}

const AccountContext = createContext<AccountContextValue | null>(null);

/** 真实 SDK 是否可用（原站注入 window.HG_ACCOUNT_SDK） */
function realSdk() {
  const w = window as unknown as { HG_ACCOUNT_SDK?: unknown };
  return w.HG_ACCOUNT_SDK ?? null;
}

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AccountState>({
    ready: false,
    loading: true,
    role: null,
    account: null,
    isAccount: false,
    channelSelectVisible: false,
  });
  const [mode, setMode] = useState<'real' | 'snapshot'>('snapshot');

  useEffect(() => {
    let alive = true;
    const boot = async () => {
      const sdk = realSdk();
      if (sdk) {
        setMode('real');
        // 真实 SDK 的初始化由 SDK 自身驱动；这里只标记就绪
        if (alive) {
          setState((s) => ({ ...s, ready: true, loading: false }));
        }
        return;
      }
      // 使用抓取的账号快照
      if (alive) {
        setState({
          ready: true,
          loading: false,
          role: SNAPSHOT_ROLE,
          account: SNAPSHOT_ACCOUNT,
          isAccount: true,
          channelSelectVisible: false,
        });
      }
    };
    void boot();
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(() => {
    setState((s) => ({ ...s, channelSelectVisible: true }));
  }, []);

  const logout = useCallback(async () => {
    setState((s) => ({ ...s, role: null, account: null, isAccount: false }));
  }, []);

  const switchRole = useCallback(() => {
    setState((s) => ({ ...s, channelSelectVisible: !s.channelSelectVisible }));
  }, []);

  const value = useMemo<AccountContextValue>(() => {
    const raw = state.role?.nickName ?? null;
    const nickname = raw ? raw.split('#')[0] || COPY.defaultNickname : COPY.defaultNickname;
    return {
      ...state,
      login,
      logout,
      switchRole,
      displayName: raw,
      nickname,
      mode,
    };
  }, [state, login, logout, switchRole, mode]);

  // 预取以保持与抓取快照一致的引用（避免 tree-shaking 掉 data 文件里的 meta）
  void (snapshot as { meta?: unknown }).meta;

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount 必须在 <AccountProvider> 内使用');
  return ctx;
}
